// Athena conversation state, shared by the side panel and the full
// page (/shell/copilot).
//
// Mounted once above the routes (App.tsx, inside the tenant auth
// provider), so the conversation and the panel's open state survive
// moving between pages. Before this, Athena was a page of its own and
// asking her anything meant leaving what you were looking at.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthContext';
import {
  askAthenaChatStream,
  fetchAthenaPersona,
  getCopilotElevation,
  grantCopilotElevation,
  revokeCopilotElevation,
  type AthenaPersona,
  type AthenaToolCall,
} from '../../lib/copilot';
import { toApiHistory } from '../../lib/athenaHistory';

export interface UiMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  refused?: boolean;
  /** MP252 — Tools Athena called to answer this turn. */
  toolCalls?: AthenaToolCall[];
  /** MP267 — Client-side timestamp for the relative time hint. */
  createdAt: number;
}

/** MP283 — Live tool-use state shown while a turn is in flight. */
export interface InflightToolCall {
  name: string;
  round: number;
  isError: boolean;
  done: boolean;
  durationMs?: number;
}

export interface AthenaPage {
  path: string;
  title?: string;
}

export const TURN_MAX_LEN = 4000;
export const STARTER_ID = 0;

interface AthenaContextValue {
  /** Owner or admin of the active org: Athena is admin-only. */
  isAdmin: boolean;
  isSuperadmin: boolean;
  persona: AthenaPersona | null;
  messages: UiMessage[];
  sending: boolean;
  phase: 'idle' | 'thinking' | 'responding';
  inflightToolCalls: InflightToolCall[];
  error: string | null;
  elevation: { elevated: boolean; remainingSeconds?: number };
  elevationBusy: boolean;
  /** Loads the persona and greeting the first time a chat view shows. */
  start: () => void;
  send: (text: string, page?: AthenaPage) => Promise<void>;
  /** Clears the conversation and starts again with the greeting. */
  reset: () => void;
  toggleElevation: () => Promise<void>;
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
}

const AthenaContext = createContext<AthenaContextValue | null>(null);

export function useAthena(): AthenaContextValue {
  const ctx = useContext(AthenaContext);
  if (!ctx) throw new Error('useAthena must be used inside <AthenaProvider>');
  return ctx;
}

/** Same as useAthena, but null outside the provider (legacy chrome). */
export function useOptionalAthena(): AthenaContextValue | null {
  return useContext(AthenaContext);
}

const PANEL_OPEN_KEY = 'xone.athenaPanelOpen';

function greeting(p: AthenaPersona): UiMessage {
  return {
    id: STARTER_ID,
    role: 'assistant',
    content:
      `I'm ${p.name}, your platform copilot. Ask me anything about X-One: how to configure a connector, ` +
      `what a posture policy does, where to find a setting, or how to triage a failing check. ` +
      `Opened from the side panel, I know which page you're on, so “this page” works. I can also pull live ` +
      `posture data; try ` +
      `“how many devices are non-compliant”. When I call a tool you'll see a small chip above my answer ` +
      `so you can check what I looked at.`,
    createdAt: Date.now(),
  };
}

export function AthenaProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const orgId = session?.activeOrgId ?? null;
  const isAdmin =
    !!session?.orgRoles && (session.orgRoles.includes('owner') || session.orgRoles.includes('admin'));
  // MP279 — Only platform_superadmin can toggle privileged mode; the
  // server enforces this too.
  const isSuperadmin = !!session?.platformRoles?.includes('platform_superadmin');

  const [started, setStarted] = useState(false);
  const [persona, setPersona] = useState<AthenaPersona | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inflightToolCalls, setInflightToolCalls] = useState<InflightToolCall[]>([]);
  const [phase, setPhase] = useState<'idle' | 'thinking' | 'responding'>('idle');
  const [elevation, setElevation] = useState<{ elevated: boolean; remainingSeconds?: number }>({
    elevated: false,
  });
  const [elevationBusy, setElevationBusy] = useState(false);
  // MP279 — Client-owned session id, stable per browser tab.
  const sessionId = useMemo(() => generateSessionId(), []);
  const [panelOpen, setPanelOpenState] = useState<boolean>(() => {
    try {
      return window.sessionStorage.getItem(PANEL_OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const setPanelOpen = useCallback((open: boolean) => {
    setPanelOpenState(open);
    try {
      window.sessionStorage.setItem(PANEL_OPEN_KEY, open ? '1' : '0');
    } catch {
      // Storage blocked: the panel still works for this page view.
    }
  }, []);

  const start = useCallback(() => setStarted(true), []);

  // A conversation belongs to one org: switching org starts a new one,
  // so answers about one tenant never sit above questions about another.
  const orgRef = useRef(orgId);
  useEffect(() => {
    if (orgRef.current === orgId) return;
    orgRef.current = orgId;
    setMessages(persona ? [greeting(persona)] : []);
    setError(null);
    setElevation({ elevated: false });
  }, [orgId, persona]);

  useEffect(() => {
    if (!started || !session || !isAdmin || persona) return;
    let cancelled = false;
    void (async () => {
      try {
        const p = await fetchAthenaPersona(await session.user.getIdToken());
        if (cancelled) return;
        setPersona(p);
        setMessages((cur) => (cur.length === 0 ? [greeting(p)] : cur));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [started, session, isAdmin, persona]);

  // Read through a ref so send() always appends to the latest history,
  // even when called from a closure made before the last reply landed.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const sendingRef = useRef(false);

  const send = useCallback(
    async (text: string, page?: AthenaPage) => {
      if (!session || !orgId || !text.trim() || sendingRef.current) return;
      sendingRef.current = true;
      setError(null);
      const userMsg: UiMessage = {
        id: Date.now(),
        role: 'user',
        content: text.slice(0, TURN_MAX_LEN),
        createdAt: Date.now(),
      };
      const next = [...messagesRef.current, userMsg];
      setMessages(next);
      setSending(true);
      setPhase('thinking');
      setInflightToolCalls([]);
      try {
        const idToken = await session.user.getIdToken();
        // MP283 — Streaming send. Progress events update the spinner
        // text and tool chips; `done` carries the reply.
        await askAthenaChatStream(
          idToken,
          orgId,
          toApiHistory(next, STARTER_ID),
          sessionId,
          (ev) => {
            if (ev.type === 'tool_call') {
              setInflightToolCalls((cur) => [...cur, { name: ev.name, round: ev.round, isError: false, done: false }]);
              setPhase('thinking');
            } else if (ev.type === 'tool_result') {
              setInflightToolCalls((cur) => {
                const idx = cur.findIndex((c) => !c.done && c.name === ev.name && c.round === ev.round);
                if (idx === -1) {
                  return [
                    ...cur,
                    { name: ev.name, round: ev.round, isError: ev.isError, done: true, durationMs: ev.durationMs },
                  ];
                }
                const clone = cur.slice();
                clone[idx] = { ...clone[idx], isError: ev.isError, done: true, durationMs: ev.durationMs };
                return clone;
              });
            } else if (ev.type === 'responding') {
              setPhase('responding');
            } else if (ev.type === 'done') {
              setMessages((cur) => [
                ...cur,
                {
                  id: Date.now() + 1,
                  role: 'assistant',
                  content: ev.message,
                  refused: ev.refused,
                  toolCalls: ev.toolCalls,
                  createdAt: Date.now(),
                },
              ]);
            } else if (ev.type === 'error') {
              setError(ev.message);
            }
          },
          page,
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        sendingRef.current = false;
        setSending(false);
        setPhase('idle');
        setInflightToolCalls([]);
      }
    },
    [session, orgId, sessionId],
  );

  const reset = useCallback(() => {
    if (sendingRef.current) return;
    setMessages(persona ? [greeting(persona)] : []);
    setError(null);
  }, [persona]);

  // MP279 — Elevation state, then a 1s countdown while elevated. The
  // server-side doc expires on its own too.
  useEffect(() => {
    if (!started || !session || !orgId || !isSuperadmin) return;
    let cancelled = false;
    void (async () => {
      try {
        const status = await getCopilotElevation(await session.user.getIdToken(), orgId, sessionId);
        if (!cancelled) setElevation({ elevated: status.elevated, remainingSeconds: status.remainingSeconds });
      } catch {
        // Non-fatal: chat still works, unelevated.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [started, session, orgId, isSuperadmin, sessionId]);

  useEffect(() => {
    if (!elevation.elevated || !elevation.remainingSeconds) return;
    const t = setInterval(() => {
      setElevation((cur) => {
        if (!cur.elevated || cur.remainingSeconds === undefined) return cur;
        const left = cur.remainingSeconds - 1;
        return left <= 0 ? { elevated: false } : { ...cur, remainingSeconds: left };
      });
    }, 1000);
    return () => clearInterval(t);
  }, [elevation.elevated, elevation.remainingSeconds]);

  const toggleElevation = useCallback(async () => {
    if (!session || !orgId) return;
    setElevationBusy(true);
    setError(null);
    try {
      const idToken = await session.user.getIdToken();
      if (elevation.elevated) {
        await revokeCopilotElevation(idToken, orgId, sessionId);
        setElevation({ elevated: false });
      } else {
        const status = await grantCopilotElevation(idToken, orgId, sessionId);
        setElevation({ elevated: status.elevated, remainingSeconds: status.remainingSeconds });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setElevationBusy(false);
    }
  }, [session, orgId, elevation.elevated, sessionId]);

  const value = useMemo<AthenaContextValue>(
    () => ({
      isAdmin,
      isSuperadmin,
      persona,
      messages,
      sending,
      phase,
      inflightToolCalls,
      error,
      elevation,
      elevationBusy,
      start,
      send,
      reset,
      toggleElevation,
      panelOpen,
      setPanelOpen,
    }),
    [
      isAdmin,
      isSuperadmin,
      persona,
      messages,
      sending,
      phase,
      inflightToolCalls,
      error,
      elevation,
      elevationBusy,
      start,
      send,
      reset,
      toggleElevation,
      panelOpen,
      setPanelOpen,
    ],
  );

  return <AthenaContext.Provider value={value}>{children}</AthenaContext.Provider>;
}

// MP279 — Session id. Hyphens stripped to keep it tidy against the
// server's [A-Za-z0-9_-]{8,64} regex.
function generateSessionId(): string {
  const raw =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return raw.replace(/-/g, '').slice(0, 32);
}
